import React from "react";
import { useTranslation } from "react-i18next";
import { useMachine } from "@xstate/react";
import truncate from "lodash/truncate";
import { PageLayout } from "@components";
import { LoadingProgressBar } from "@rentablo/components";
import { useMyself } from "@api/v1/hooks/useMyself";
import { SomethingWrong } from "./steps/SomethingWrong";
import { PriceAndDateStep } from "./steps/PriceAndDateStep";
import { BookingValues } from "./stateMachine/types/bookingValues";
import { SummaryStep } from "./steps/SummaryStep";
import { PersistedStep } from "./steps/PersistedStep";
import { useStateHelpers } from "./hooks/useStateHelpers";
import { editTransactionWizardSM } from "./stateMachine/editTransactionWizardSM";

export interface Props {
  id: number;
}

export const EditTransactionWizard = ({ id }: Props) => {
  const [currentState, sendEvent] = useMachine(editTransactionWizardSM);
  const { isLoadingState } = useStateHelpers(currentState);
  useMyself();

  const backOneStep = () => sendEvent({ type: "BACK" });

  const { t } = useTranslation();
  return (
    <PageLayout
      breadcrumbs={[
        { text: t("breadcrumbs.home"), to: "/" },
        { to: "/investments", text: t("breadcrumbs.investments") },
        {
          to: `/investments/${id}`,
          text: truncate(currentState.context.investmentValues?.name),
        },
        { text: t("transactionWizard.edit.title") },
      ]}
    >
      {isLoadingState() && <LoadingProgressBar />}

      {currentState.matches("idle") &&
        sendEvent({ type: "INIT_FROM_BOOKING", id: id }) && (
          <LoadingProgressBar />
        )}

      {currentState.matches("priceAndDate") && (
        <PriceAndDateStep
          handleNext={(bookingValues: BookingValues) =>
            sendEvent({
              type: "VALIDATE_BOOKING",
              bookingValues,
            })
          }
          bookingValues={currentState.context.bookingValues}
          bookingValuesToRestore={currentState.context.bookingValuesToRestore}
          investmentValues={currentState.context.investmentValues}
          validationError={currentState.context.validationError}
          isEditTransaction
        />
      )}

      {currentState.matches("summary") && (
        <SummaryStep
          handleNext={() =>
            sendEvent({
              type: "PERSIST",
            })
          }
          handlePrevious={backOneStep}
          bookingValues={currentState.context.bookingValues}
          investmentType={
            currentState.context.investmentValues.type || "11_stock"
          }
          investmentCurrency={
            currentState.context.investmentValues.investmentCurrency || "EUR"
          }
          quoteNotation={currentState.context.investmentValues.quoteNotation}
        />
      )}

      {currentState.matches("persisted") && (
        <PersistedStep
          redirectUrl={`/investments/${currentState.context.investmentValues.id}?tab=protocol`}
          investmentName={currentState.context.investmentValues.name || ""}
        />
      )}

      {currentState.matches("somethingWrong") && (
        <SomethingWrong message={currentState.context.error?.message} />
      )}
    </PageLayout>
  );
};
